import { BarChart3, Loader2 } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { SettingsModalLayout } from "@/features/user-setting/components/SettingsModalLayout";
import { useUploadQuota } from "@/hooks/useUploadQuota";

interface UsageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UsageModal: React.FC<UsageModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch, error } = useUploadQuota(isOpen);

  if (!isOpen) return null;

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";

    const units = ["B", "KB", "MB", "GB", "TB"];
    const exponent = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      units.length - 1,
    );
    const value = bytes / 1024 ** exponent;
    const formatted =
      value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1);

    return `${formatted} ${units[exponent]}`;
  };

  const usagePercent =
    data && !data.is_paid_plan ? Math.round(data.usage_ratio * 100) : 0;

  return (
    <SettingsModalLayout
      isOpen={isOpen}
      onClose={onClose}
      icon={BarChart3}
      maxWidth="max-w-md"
      title={t("common.usage")}
      footer={
        <div className="flex justify-end border-t bg-gray-50 p-6">
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
          <Loader2 className="size-5 animate-spin" />
          <span>{t("common.loading")}</span>
        </div>
      ) : isError ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error
              ? error.message
              : t("common.usageLoadError")}
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("common.uploadUsage", "Upload usage")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("common.uploadUsageDescription", "Usage for this month")}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">
                  {t("common.currentMonth", "Current month")}
                </p>
                <p className="text-base font-semibold text-gray-900">
                  {data.month_key}
                </p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-700 ring-1 ring-gray-200">
                {data.is_paid_plan
                  ? t("common.paidPlan", "Paid plan")
                  : t("common.freePlan", "Free plan")}
              </span>
            </div>

            {data.is_paid_plan ? (
              <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
                <p className="text-sm text-gray-500">
                  {t("common.uploadLimit", "Upload limit")}
                </p>
                <p className="text-base font-semibold text-gray-900">
                  {t("common.unlimitedCapacity", "Unlimited")}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {t("common.usedCapacity", "Used")}
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatBytes(data.used_bytes)} /{" "}
                    {formatBytes(data.limit_bytes)}
                  </span>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-rw-main transition-[width]"
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </SettingsModalLayout>
  );
};
